import {
  CommandBarButton,
  IDropdownOption,
  Stack,
  Text,
} from '@fluentui/react';
import React from 'react';
import { SupplierCommonData_countries_nodes } from '../../../__generated__/SupplierCommonData';
import { useStyles } from './index.styles';
import { FormHookCountryRegion, FormHookTextField } from 'common/components/FormHooksFields';
import { FormHookCheckBox } from 'common/components/FormHooksFields/FormHookCheckBox';
import { SupplierValues } from '../../../types';
import { UseFormSetValue } from 'react-hook-form';

type AddressName = `supplierAddressesBySupplierId.${number}`;

interface AddressFormFieldsProps {
  addressName: AddressName;
  toList: () => void;
  countries: SupplierCommonData_countries_nodes[];
  isUpdatable: boolean | null | undefined;
  setValue: UseFormSetValue<SupplierValues>;
}

export const AddressFormFields: React.FC<AddressFormFieldsProps> = ({
  addressName,
  toList,
  countries,
  isUpdatable,
  setValue,
}) => {
  const styles = useStyles();
  const countryOptions: IDropdownOption[] =
    countries.map((ele) => ({
      key: ele.id,
      text: ele.country,
    })) || [];
  const isDisabled = isUpdatable === false ? true : false;
  return (
    <>
      <Stack
        horizontal
        horizontalAlign="space-between"
        className={styles.defaultButtonContainer}
      >
        <CommandBarButton
          text="Address List"
          iconProps={{ iconName: 'Back' }}
          onClick={() => {
            toList();
          }}
          style={{
            width: 120,
            height: 40,
          }}
        />
      </Stack>
      <Text variant={'xLarge'}>Address</Text>
      <Stack
        horizontal
        tokens={{
          childrenGap: 20,
        }}
        className={styles.formRow}
      >
        <Stack.Item className={styles.fieldContainer}>
          <FormHookTextField
            name={`${addressName}.addressLine1`}
            label="Line 1)"
            placeholder="Street and Number, P.O. Box"
            disabled={isDisabled}
          />
        </Stack.Item>
        <Stack.Item className={styles.fieldContainer}>
          <FormHookTextField
            name={`${addressName}.addressLine2`}
            label="Line 2)"
            placeholder="Apartment, Suite, Unit, Building, Floor"
            disabled={isDisabled}
          />
        </Stack.Item>
        <Stack.Item className={styles.fieldContainer}>
          <FormHookTextField
            name={`${addressName}.cityName`}
            label="City"
            placeholder="City, Town"
            disabled={isDisabled}
          />
        </Stack.Item>
      </Stack>
      <Stack
        horizontal
        tokens={{
          childrenGap: 20,
        }}
        className={styles.formRow}
      >
        <Stack.Item style={{ flex: 2 }}>
          <FormHookCountryRegion
            countries={countryOptions}
            countryName={`${addressName}.countryId`}
            regionName={`${addressName}.stateRegionId`}
            CountryAutocompleteProps={{
              label: 'Country',
              errorMessage: '',
            }}
            StateRegionAutocompleteProps={{
              label: 'State / Region / Province',
              errorMessage: '',
            }}
            onSelectCountry={(country) =>
              setValue(`${addressName}.country.country`, country)
            }
            onSelectState={(state) =>
              setValue(`${addressName}.stateRegion.stateRegion`, state)
            }
            disabled={isDisabled}
          />
        </Stack.Item>
        <Stack.Item className={styles.fieldContainer}>
          <FormHookTextField
            name={`${addressName}.postalCode`}
            label="Zip/Postal Code"
            placeholder="Zip/Postal Code"
            disabled={isDisabled}
          />
        </Stack.Item>
      </Stack>
      <Stack
        horizontal
        tokens={{
          childrenGap: 20,
        }}
        className={styles.formRow}
      >
        <Stack.Item className={styles.fieldContainer}>
          <FormHookTextField
            name={`${addressName}.telephoneNumber`}
            label="Phone"
            placeholder="Phone"
            disabled={isDisabled}
          />
        </Stack.Item>
        <Stack.Item className={styles.fieldContainer}>
          <FormHookTextField
            name={`${addressName}.emailAddress`}
            label="Email"
            placeholder="Email"
            disabled={isDisabled}
          />
        </Stack.Item>
        <Stack horizontal verticalAlign="end" className={styles.fieldContainer}>
          <Stack.Item className={styles.sendCheckBoxContainer}>
            <FormHookCheckBox
              name={`${addressName}.isSendEmailAccount`}
              label="Send"
              disabled={isDisabled}
            />
          </Stack.Item>
          <Stack.Item>
            <FormHookCheckBox
              name={`${addressName}.isReceiveEmailAccount`}
              label="Receive"
              disabled={isDisabled}
            />
          </Stack.Item>
        </Stack>
      </Stack>
      <Stack horizontal className={styles.formRow}>
        <Stack.Item className={styles.websiteFieldContainer}>
          <FormHookTextField
            name={`${addressName}.webAddress`}
            label="Website"
            placeholder="Website"
            disabled={isDisabled}
          />
        </Stack.Item>
      </Stack>
    </>
  );
};
